import { Path, UseFormReturn } from "react-hook-form"
import * as Yup from "yup"

import getApiErrorMessages from "@utils/getApiErrorMessages"

import toast from "@molecules/Toast/Toast"

import { EcommerceApiErrorResponse } from "@pages/Settings/SettingsEcommerce/SettingsEcommerce.types"

import { EcommerceConnectFormValues } from "./EcommerceConnectDialog.types"

export function buildConnectFormSchema(platform: EcommercePlatformDetails) {
    return Yup.object().shape({
        store_name: Yup.string().required("Please enter a name for this store."),
        credentials: Yup.object().shape(
            Object.fromEntries(
                platform.credential_fields.map((credentialField) => [
                    credentialField.name,
                    Yup.string().required(`Please enter the ${credentialField.label.toLowerCase()}.`),
                ]),
            ),
        ),
        customer_id: Yup.string(),
        // The company name is only meaningful as the binding confirmation for an entered customer id, so
        // it is required exactly when a customer id is present and otherwise left optional.
        company_name: Yup.string().when("customer_id", {
            is: (value: string | undefined) => Boolean(value && value.trim()),
            then: (schema) => schema.required("Please enter your company name to confirm the customer."),
            otherwise: (schema) => schema,
        }),
    })
}

function getPrefilledCredentialValue(
    credentialField: EcommerceCredentialField,
    connection?: EcommerceConnection | null,
): string {
    if (!connection || credentialField.is_secret) {
        return ""
    }

    // The read shape never includes credentials, so only non-secret fields mirrored onto the
    // connection itself (currently the store identifier) can be prefilled when re-credentialing.
    if (credentialField.name === "store_identifier") {
        return connection.store_identifier
    }

    return ""
}

export function buildConnectDefaultValues(
    platform: EcommercePlatformDetails,
    connection: EcommerceConnection | null | undefined,
    companyName: string,
): EcommerceConnectFormValues {
    return {
        store_name: connection?.store_name ?? "",
        credentials: Object.fromEntries(
            platform.credential_fields.map((credentialField) => [
                credentialField.name,
                getPrefilledCredentialValue(credentialField, connection),
            ]),
        ),
        // The bound customer id round-trips on the read shape, so a re-credentialing edit reopens on the
        // customer that is already attributed. The company name isn't stored — it is prefilled from the
        // service company's own name as the starting point for the binding confirmation.
        customer_id: connection?.customer_id ?? "",
        company_name: companyName,
    }
}

/**
 * Applies a connect endpoint error response onto the schema-driven connect form.
 *
 * `store_name` errors and `credentials.<field>` errors for declared credential fields are surfaced
 * inline via `form.setError`. `platform` errors, `non_field_errors`, and credential errors that don't
 * match a declared field are routed to `setNonFieldError` for the form's bottom alert banner. When the
 * response contains nothing recognizable, an error toast is fired with `genericMessage`.
 */
export function applyConnectApiErrorsToForm(
    form: UseFormReturn<EcommerceConnectFormValues>,
    error: EcommerceApiErrorResponse,
    platform: EcommercePlatformDetails,
    genericMessage: string,
    setNonFieldError: (message: string) => void,
): void {
    let hasHandledError = false

    const storeNameMessages = getApiErrorMessages(error.store_name)
    if (storeNameMessages.length > 0) {
        form.setError("store_name", { type: "server", message: storeNameMessages[0] })
        hasHandledError = true
    }

    const customerIdMessages = getApiErrorMessages(error.customer_id)
    if (customerIdMessages.length > 0) {
        form.setError("customer_id", { type: "server", message: customerIdMessages[0] })
        hasHandledError = true
    }

    const declaredFieldNames = platform.credential_fields.map((credentialField) => credentialField.name)
    for (const [fieldName, value] of Object.entries(error.credentials ?? {})) {
        const messages = getApiErrorMessages(value)
        if (messages.length === 0) {
            continue
        }

        if (declaredFieldNames.includes(fieldName)) {
            const formField: Path<EcommerceConnectFormValues> = `credentials.${fieldName}`
            form.setError(formField, { type: "server", message: messages[0] })
        } else {
            setNonFieldError(messages[0])
        }
        hasHandledError = true
    }

    const nonFieldMessages = [...getApiErrorMessages(error.platform), ...getApiErrorMessages(error.non_field_errors)]
    if (nonFieldMessages.length > 0) {
        setNonFieldError(nonFieldMessages[0])
        hasHandledError = true
    }

    if (!hasHandledError) {
        toast({ type: "error", title: genericMessage })
    }
}
